import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import { VISITOR_STATUS, VISITOR_TYPE, metaFor, optionsFrom, type Visitor } from "@/lib/adminModules";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate, formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminVisitors() {
  const [rows, setRows] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Visitor>("visitors", "total_spend"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load visitors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Visitor>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("visitors", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (typeFilter !== "all" && row.visitor_type !== typeFilter) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.name, row.email || "", row.phone || "", row.location || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, typeFilter, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const totalSpend = rows.reduce((sum, row) => sum + Number(row.total_spend || 0), 0);
  const repeat = rows.filter((row) => row.total_bookings > 1).length;

  const columns: Column<Visitor>[] = [
    {
      key: "name",
      header: "Visitor",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold shrink-0">
            {row.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{row.email || row.phone || "—"}</p>
          </div>
        </div>
      ),
    },
    { key: "type", header: "Type", render: (row) => <StatusPill {...metaFor(VISITOR_TYPE, row.visitor_type)} /> },
    {
      key: "location",
      header: "Location",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.location || "—"}</span>,
    },
    { key: "bookings", header: "Bookings", render: (row) => <span className="text-sm text-foreground-700">{row.total_bookings}</span> },
    { key: "visits", header: "Visits", render: (row) => <span className="text-sm text-foreground-700">{row.total_visits}</span> },
    {
      key: "spend",
      header: "Spend",
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
          {formatRupees(row.total_spend)}
        </span>
      ),
    },
    {
      key: "last",
      header: "Last visit",
      render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.last_visit)}</span>,
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(VISITOR_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Customers"
        title="Visitors"
        subtitle="A full visitor CRM — every guest, their type, booking history, lifetime spend and contact details."
      />

      <StatCards
        stats={[
          { label: "Total visitors", value: rows.length, icon: "ri-user-3-line", tone: "primary" },
          { label: "Repeat visitors", value: repeat, icon: "ri-repeat-2-line", tone: "secondary" },
          { label: "Lifetime spend", value: formatRupees(totalSpend), icon: "ri-money-rupee-circle-line", tone: "primary" },
          {
            label: "Blocked",
            value: rows.filter((row) => row.status === "blocked").length,
            icon: "ri-forbid-line",
            tone: "accent",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, email, phone, city…" />
          <Select
            label="Type"
            value={typeFilter}
            onChange={setTypeFilter}
            options={[{ value: "all", label: "All types" }, ...optionsFrom(VISITOR_TYPE)]}
          />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(VISITOR_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} visitors
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading visitors…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-user-3-line"
            title={rows.length === 0 ? "No visitors yet" : "No visitors match"}
            message="Guest profiles build automatically from bookings and enquiries."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Visitor"}
        subtitle="Visitor profile"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  update(
                    selected.id,
                    { status: selected.status === "blocked" ? "active" : "blocked" },
                    selected.status === "blocked" ? "Visitor unblocked." : "Visitor blocked.",
                  )
                }
                className={selected.status === "blocked" ? btnGhost : btnAccent}
              >
                <i className={selected.status === "blocked" ? "ri-lock-unlock-line" : "ri-forbid-line"}></i>
                {selected.status === "blocked" ? "Unblock visitor" : "Block visitor"}
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(VISITOR_TYPE, selected.visitor_type)} />
              <StatusPill {...metaFor(VISITOR_STATUS, selected.status)} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">{selected.total_bookings}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Bookings</p>
              </div>
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">{selected.total_visits}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Visits</p>
              </div>
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">
                  {formatRupees(selected.total_spend)}
                </p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Spend</p>
              </div>
            </div>

            <DetailList
              items={[
                { icon: "ri-phone-line", label: "Phone", value: selected.phone || "—" },
                { icon: "ri-mail-line", label: "Email", value: selected.email || "—" },
                { icon: "ri-map-pin-line", label: "Location", value: selected.location || "—" },
                { icon: "ri-calendar-line", label: "Last visit", value: formatDate(selected.last_visit) },
              ]}
            />

            <div className="flex flex-wrap gap-2">
              {selected.email ? (
                <a href={`mailto:${selected.email}`} className={btnGhost}>
                  <i className="ri-mail-line"></i> Email
                </a>
              ) : null}
              {selected.phone ? (
                <a href={`tel:${selected.phone}`} className={btnGhost}>
                  <i className="ri-phone-line"></i> Call
                </a>
              ) : null}
            </div>

            <Field label="Notes">
              <textarea
                rows={4}
                value={selected.notes || ""}
                onChange={(event) =>
                  setRows((list) =>
                    list.map((row) => (row.id === selected.id ? { ...row, notes: event.target.value } : row)),
                  )
                }
                onBlur={() => update(selected.id, { notes: selected.notes })}
                placeholder="Preferences, allergies, follow-ups, VIP status…"
                className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              ></textarea>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Notes and status save automatically.
      </p>
      {node}
    </div>
  );
}